import React, { useCallback, useEffect, useState } from "react";
import { Loader2, RefreshCw, ExternalLink, Unplug, Link2 } from "lucide-react";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { confirmAsync } from "@/lib/safeConfirm";
import { Card, CopyButton, btnDark, btnLight } from "./atoms";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" }) : "—");

export function GoogleSheetCard() {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(null);
  const [params, setParams] = useSearchParams();

  const load = useCallback(async () => {
    try { const { data } = await api.get("/integrations/google/status"); setSt(data); }
    catch { setSt({ configured: false, connected: false }); }
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const g = params.get("google");
    if (!g) return;
    if (g === "connected") toast.success("Google conectado · creando la hoja de estadísticas…");
    else toast.error(`Google no se pudo conectar (${params.get("msg") || "error"})`);
    params.delete("google"); params.delete("msg");
    setParams(params, { replace: true });
    const t = setTimeout(load, 6000);
    return () => clearTimeout(t);
  }, [params, setParams, load]);

  const connect = async () => {
    setBusy("connect");
    try { const { data } = await api.get("/integrations/google/login"); window.location.href = data.url; }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo iniciar la conexión"); setBusy(null); }
  };
  const syncNow = async () => {
    setBusy("sync");
    try { const { data } = await api.post("/integrations/google/sync"); toast.success(`Hoja actualizada · ${data.rows} filas`); setSt(data.status); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo actualizar"); load(); }
    finally { setBusy(null); }
  };
  const disconnect = async () => {
    if (!(await confirmAsync("La hoja seguirá en tu Drive, pero dejará de actualizarse. ¿Desconectar Google?", { destructive: true, confirmLabel: "Desconectar" }))) return;
    setBusy("disc");
    try { await api.post("/integrations/google/disconnect"); toast.success("Google desconectado"); load(); }
    catch { toast.error("No se pudo desconectar"); }
    finally { setBusy(null); }
  };

  return (
    <Card kicker="ChatGPT" title="Estadísticas en Google Sheets" testid="google-sheet-card">
      <p className="text-sm text-clay-700 -mt-2">
        Mantenemos una hoja de cálculo en tu Google Drive con las estadísticas de Curated Trips (Diario, Embudo por destino,
        Fuentes, Campañas y Viajes), actualizada <strong>cada día a las 7:00</strong>. En ChatGPT, conecta Google Drive
        (Ajustes → Aplicaciones conectadas) o pégale el enlace de la hoja y pregúntale lo que quieras.
      </p>
      {!st ? (
        <div className="flex items-center gap-2 text-sm text-clay-600"><Loader2 size={14} className="animate-spin"/> Cargando…</div>
      ) : !st.configured ? (
        <div className="text-sm border border-amber-300 bg-amber-50 text-amber-900 p-3" data-testid="google-not-configured">
          Faltan las credenciales de Google (GOOGLE_CLIENT_ID / GOOGLE_CLIENT_SECRET). Añádelas y vuelve aquí para conectar.
        </div>
      ) : !st.connected ? (
        <div className="space-y-3">
          <button onClick={connect} disabled={busy === "connect"} className={btnDark} data-testid="google-connect-btn">
            {busy === "connect" ? <Loader2 size={12} className="animate-spin"/> : <Link2 size={12}/>} Conectar Google (eduardo@viajadverdad.com)
          </button>
          <p className="text-[12px] text-clay-600">
            Se abrirá Google para autorizar «Ver, editar y crear hojas de cálculo». La hoja se crea en el Drive de la cuenta que autorices.
          </p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="google-connected">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
            <div className="border border-clay-200 bg-clay-50 p-3"><div className="smallcaps">Cuenta</div><div className="mt-1 break-all" data-testid="google-email">{st.email || st.connected_by}</div></div>
            <div className="border border-clay-200 bg-clay-50 p-3"><div className="smallcaps">Última actualización</div><div className="mt-1" data-testid="google-last-sync">{fmt(st.last_sync_at)}{st.last_sync_rows ? ` · ${st.last_sync_rows} filas` : ""}</div></div>
            <div className="border border-clay-200 bg-clay-50 p-3"><div className="smallcaps">Próxima</div><div className="mt-1">Mañana 7:00 (Madrid)</div></div>
          </div>
          {st.last_sync_error && (
            <div className="text-[12px] border border-terracotta/40 bg-terracotta/5 text-clay-900 p-3 font-mono break-all" data-testid="google-sync-error">✗ {st.last_sync_error}</div>
          )}
          <div className="flex items-center gap-2 flex-wrap">
            {st.spreadsheet_url ? (
              <>
                <a href={st.spreadsheet_url} target="_blank" rel="noreferrer" className={btnDark} data-testid="google-open-sheet"><ExternalLink size={12}/> Abrir la hoja</a>
                <CopyButton text={st.spreadsheet_url} testid="google-copy-sheet-url" label="Copiar enlace"/>
              </>
            ) : (
              <span className="text-sm text-clay-600 inline-flex items-center gap-2"><Loader2 size={12} className="animate-spin"/> Creando la hoja…</span>
            )}
            <button onClick={syncNow} disabled={busy === "sync"} className={btnLight} data-testid="google-sync-btn">
              {busy === "sync" ? <Loader2 size={12} className="animate-spin"/> : <RefreshCw size={12}/>} Actualizar ahora
            </button>
            <button onClick={disconnect} disabled={busy === "disc"} className={btnLight} data-testid="google-disconnect-btn"><Unplug size={12}/> Desconectar</button>
          </div>
        </div>
      )}
    </Card>
  );
}
